/**
 * The studio draft: the whole workspace-to-be of one setup session, in the
 * full RecordOS vocabulary, as the server stores it per version and every
 * client draws it (Phase 2 of docs/plans/onboarding-harness.md).
 *
 * It reuses the setup builder's record, field, tile, formula and seed-row
 * shapes unchanged (`compose-author.ts`), so one author turns it into the
 * `ComposedSetup` compose already writes, and it adds what a `SetupDraft`
 * cannot hold: cards across records; roles with their access; imports; the sources
 * the person brought; notes; what RecordOS cannot do yet; where each part
 * came from; and the gaps the person chose to leave.
 *
 * Pure: no clock, no I/O, no words. Every part is addressed by a KEY that
 * never changes once minted (paths.ts); a rename changes words only.
 */
import type { AgentLanguageTag } from '../agent-language';
import { COMPOSE_LIMITS, type UnmetNeedLevel } from '../compose';
import type {
  SETUP_TILE_SCOPES,
  SetupDraftFormula,
  SetupDraftRecord,
  SetupDraftTile,
} from '../compose-author';
import type { FormulaCalcOp } from '../formula';
import type { ColumnTransform, ImportRowKind } from '../imports';
import type { InsightAgg, InsightCardId, InsightFilterOp } from '../insights';
import type { AccessLevel, PermissionKey, Reach } from '../roles';
import type { CapabilityId } from '../manifest/ids';
import type { StudioPartRef } from './paths';

/** The draft's own shape number: a stored snapshot of another shape is read by a migration, never guessed at. */
export const STUDIO_DRAFT_SHAPE = 1;

export type StudioMeta = {
  /** '' until the person gives one: a readiness blocker, never an op refusal. */
  name: string;
  /** ISO 4217. */
  currency: string;
  language: AgentLanguageTag;
  /** "Kothrud, Pune", at most 120 characters. */
  place?: string;
  /** What the business does, in one sentence, at most 280 characters. */
  does?: string;
  /**
   * The business's own website, an `https` URL of at most 300 characters: set
   * only when the person gave it or confirmed a place's website fact (3.11).
   * The one site besides the person's own links that OS may read.
   */
  site?: string;
};

/** A record's own card, with a key of its own so an op can address it. */
export type StudioTile = SetupDraftTile & { key: string };

/**
 * A record or a list (`kind` 'record' | 'directory'), its cards keyed.
 *
 * `headless`: a built record or list with no field that can name a row (a
 * shop's Daily sales of an Amount alone), read back by Edit the setup
 * (`draftFromWorkspace(…, 'edit')`) so it is drawn and edited like any other.
 * Its rows stand unnamed in the workspace, so while no field can name a row
 * its headline is '' and the judge does not ask it for one; a field that can,
 * once added, becomes its headline (`mendDraftRecord`). Build again never
 * writes a headline. No op sets it: only the read of a built workspace does.
 */
export type StudioRecord = Omit<SetupDraftRecord, 'tiles'> & { tiles?: StudioTile[]; headless?: true };

/** Where a part came from: the person's words, a source they brought, a lookup, or OS's own choice. */
export type StudioFrom =
  | { kind: 'said'; quote?: string }
  /** `finding`: the id of the source's finding it rests on (`f3`), when the turn named one (4.13). */
  | { kind: 'source'; source: string; part?: string; quote?: string; finding?: string }
  | { kind: 'looked_up'; topic: string }
  | { kind: 'os' };

/* ------------------------------------------------------ cards across records */

/** What a card leaf totals: the record's measure, one field, or a worked-out value per row. */
export type StudioCardSource = { kind: 'measure' } | { kind: 'field'; field: string } | { kind: 'expr'; formula: SetupDraftFormula };

/** One filter on a leaf's rows: `field` on the record, or with `via` on the list a reference of it picks from. */
export type StudioCardWhere = { field: string; op: InsightFilterOp; value?: string | number | Array<string | number>; via?: string };

export type StudioCardLeaf = {
  kind: 'agg';
  agg: InsightAgg;
  record: string;
  of?: StudioCardSource;
  where?: StudioCardWhere[];
  /** A reference of the record: one row of the card per row of its list. */
  groupBy?: string;
};

export type StudioCardNode =
  | StudioCardLeaf
  | { kind: 'const'; value: number }
  | { kind: 'calc'; op: FormulaCalcOp; left: StudioCardNode; right: StudioCardNode }
  | { kind: 'round'; of: StudioCardNode; decimals: number };

/** A Dashboard card that may total several records: "What each customer owes". */
export type StudioBoardCard = {
  key: string;
  label: string;
  card: InsightCardId;
  total: StudioCardNode;
  scope?: (typeof SETUP_TILE_SCOPES)[number];
  days?: number;
  limit?: number;
};

/* ------------------------------------------------------------------- roles */

export const STUDIO_ROLE_BASES = ['admin', 'member', 'viewer'] as const;

/**
 * A role drafted with the workspace (decision 4): its people are names only,
 * invited after Build. `permissions` lays pairs over the base preset (a list,
 * because a map would not survive the model's schema); `access` says, per
 * record or list, whether the role changes it, sees it, or does not see it.
 */
export type StudioRole = {
  key: string;
  name: string;
  purpose?: string;
  people: string[];
  base: (typeof STUDIO_ROLE_BASES)[number];
  permissions: Array<{ key: PermissionKey; value: Reach | boolean }>;
  access: Array<{ record: string; level: AccessLevel }>;
};

/* ----------------------------------------------------------------- imports */

/** One column of a sheet, mapped to a top-level field of the record (null: left out). */
export type StudioImportColumn = { column: number | null; header: string; field: string | null; transform: ColumnTransform };

/** A sheet of a source the person brought, planned into one record or list (decision 3). */
export type StudioImport = {
  key: string;
  /** The source's id (`draft.sources`). */
  source: string;
  sheet: string;
  record: string;
  headerRow: number | null;
  skipRows: number[];
  columns: StudioImportColumn[];
  /** How many rows the sheet holds, when known. */
  rows?: number;
  /**
   * The record's required fields no column fills (step 2.3 of
   * docs/plans/setup-file-data.md): a list's names still come in, and these
   * wait for the review step after Build. Absent: none.
   */
  unfilled?: string[];
  /**
   * The kind of rows it takes, when the sheet mixes kinds (W8 of
   * docs/plans/workbook-map.md): a sheet has one import for each kind.
   * Absent: every row.
   */
  rowKind?: ImportRowKind;
};

/* ------------------------------------------ sources, notes and what is not yet */

/** Mine · Like this · Reference app: the app the person uses, never named back (decision 5). */
export const STUDIO_SOURCE_ROLES = ['mine', 'like', 'reference'] as const;

/** A file or link the person brought: the id of its `app.agent_file` row, and the part it plays. */
export type StudioSource = { id: string; role: (typeof STUDIO_SOURCE_ROLES)[number]; dismissed: boolean };

/** `watch`: what the person checks every day, in their words; its `card` is the Dashboard card that answers it. */
export const STUDIO_NOTE_KINDS = ['assumed', 'decided', 'from_source', 'added_for_you', 'watch'] as const;

/** A note about a part (`about` is a part path, or `workspace`), with the sources it came from. */
export type StudioNote = {
  key: string;
  about: string;
  kind: (typeof STUDIO_NOTE_KINDS)[number];
  text: string;
  sources: string[];
  /** A Dashboard card's key (`boardCards`): the card that answers a `watch` note. */
  card?: string;
};

export const STUDIO_NOT_YET_KINDS = ['record', 'field', 'link', 'card', 'role', 'other'] as const;

/** Something RecordOS cannot hold yet (decision 1), with the closest fit the draft made instead. */
export type StudioNotYet = {
  key: string;
  kind: (typeof STUDIO_NOT_YET_KINDS)[number];
  name: string;
  fit: string;
  level: UnmetNeedLevel;
  archetype: string;
  need: string;
  /** The manifest entry closest to the wish (`findCapabilities`), so the same wish is never noted twice (3.13). */
  capability?: CapabilityId;
};

/**
 * Something OS's critic noticed the draft may miss on its first day. It holds
 * no op: picking one of its chips is an answer the next turn reads.
 */
export type CriticCheck = {
  id: string;
  on: StudioPartRef | null;
  title: string;
  question: string;
  why: string;
  /** `keeps`: picking it leaves the draft as it is (Skip, Keep as is), so the check is left with no turn. */
  chips: Array<{ label: string; keeps?: true }>;
};

/* ------------------------------------------------------ the app the person uses */

/** Moving from the app (`switching`), or wanting something like it (`like`): its shape, never its words (6.14). */
export const STUDIO_APP_MAP_INTENTS = ['switching', 'like'] as const;
export const STUDIO_APP_MAP_KINDS = ['list', 'record', 'link', 'card', 'role'] as const;
/** What a part of the app became here: the same, working differently, not in RecordOS yet, or left out. */
export const STUDIO_APP_MAP_STATUSES = ['same', 'differs', 'not_yet', 'left'] as const;

/** One part of the app the person said they use, in their words, and what it became here. */
export type StudioAppMapRow = {
  /** The person's own word for the part of the app ("You gave"). Masked of the app's name before it is stored. */
  theirs: string;
  /** The part path it became (2.2), or null when no part answers it. */
  part: string | null;
  kind: (typeof STUDIO_APP_MAP_KINDS)[number] | null;
  status: (typeof STUDIO_APP_MAP_STATUSES)[number];
  /** Why it differs or was left, in one line. */
  note?: string;
  /** The `notYet` key of a `not_yet` row. */
  notYet?: string;
};

/**
 * The mapping from the app the person uses to their setup (6.14): one row per
 * part they said they use; the parts they did not mention are `notPicked` by
 * name, never rows. `source` is the `agent_file` id of the app's source, or
 * `looked:<toolCallId>` for an app only looked up. It never holds the app's name.
 */
export type StudioAppMap = {
  id: string;
  source: string;
  intent: (typeof STUDIO_APP_MAP_INTENTS)[number];
  /** How many pages the lookup read. */
  pages: number;
  rows: StudioAppMapRow[];
  notPicked: string[];
};

/* --------------------------------------------------------------- the draft */

export type StudioDraft = {
  shape: typeof STUDIO_DRAFT_SHAPE;
  meta: StudioMeta;
  /** Records and lists, in the order the person sees them. */
  records: StudioRecord[];
  /** Cards across records: the Dashboard. A record's own cards are its `tiles`. */
  boardCards: StudioBoardCard[];
  roles: StudioRole[];
  imports: StudioImport[];
  sources: StudioSource[];
  notes: StudioNote[];
  notYet: StudioNotYet[];
  /** Where each part came from, by part path. */
  from: Array<{ path: string; from: StudioFrom[] }>;
  /**
   * Gaps the person chose to leave, so the gap engine never raises them again.
   * A critic's check keeps its words (`title`, `question`) once it is left, so
   * the critic knows it in any wording, after the check itself is taken off.
   */
  dismissedGaps: Array<{ gap: string; reason?: string; title?: string; question?: string }>;
  /** What the critic noticed (`add_check`). Absent on a snapshot written before checks: read as none. */
  checks?: CriticCheck[];
  /** The app the person uses, mapped to this setup (`set_app_map`, 6.14). Absent on a snapshot written before maps: read as none. */
  appMaps?: StudioAppMap[];
  /** Conflicts between sources the person chose to decide later (`later_conflict`, 9.10), by conflict id. Absent: none. */
  laterConflicts?: string[];
};

/**
 * The language OS talks in, picked on the language card (9.12), is kept with
 * the draft as this left gap (reason `picked <tag>`): every device reads it,
 * and no gap rule raises it. A receipt says nothing of it.
 */
export const LANGUAGE_PICKED_GAP = 'language_ask:workspace';

/**
 * The hard ceilings (decision 10), read by the reducer, the judge and the
 * manifest. The ones a `ComposedSetup` also carries are read from
 * `COMPOSE_LIMITS`, so the two can never disagree.
 */
export const STUDIO_LIMITS = {
  opsPerBatch: 40,
  records: COMPOSE_LIMITS.records,
  fields: COMPOSE_LIMITS.fields,
  tilesPerRecord: 8,
  boardCards: 12,
  roles: 8,
  rolePeople: 12,
  imports: 20,
  importColumns: 60,
  sources: 40,
  notes: 80,
  notYet: COMPOSE_LIMITS.unmetNeeds,
  seedRows: COMPOSE_LIMITS.seedRows,
  fromPerPart: 6,
  quote: 160,
  noteText: 280,
  reason: 280,
  keyLength: 40,
  snapshotBytes: 524_288,
  checks: 20,
  appMaps: 4,
  appMapRows: 30,
} as const;

/**
 * What is wrong with an app map's shape, or null (6.14): more rows than
 * `appMapRows`, a row with no words of theirs, or, as the map is set
 * (`keys`), a `not_yet` row whose key the draft does not hold. A row whose
 * part or not-yet item was removed since is never a problem: the panel draws
 * it as left out.
 */
export const appMapProblem = (draft: StudioDraft, map: StudioAppMap, { keys = true }: { readonly keys?: boolean } = {}): string | null => {
  if (map.rows.length > STUDIO_LIMITS.appMapRows) return `An app map holds ${STUDIO_LIMITS.appMapRows} rows at most; this one holds ${map.rows.length}.`;
  const blank = map.rows.findIndex((row) => row.theirs.trim() === '');
  if (blank !== -1) return `Row ${blank + 1} of the app map says nothing of the app's own words.`;
  if (!keys) return null;
  const unknown = map.rows.find((row) => row.status === 'not_yet' && (row.notYet === undefined || !draft.notYet.some((item) => item.key === row.notYet)));
  if (unknown !== undefined) return `A row not in RecordOS yet names "${unknown.notYet ?? ''}", which is not a not-yet item of this draft.`;
  return null;
};

/** A draft with nothing in it yet: no name, no parts. */
export const emptyStudioDraft = ({ currency, language }: { currency: string; language: AgentLanguageTag }): StudioDraft => ({
  shape: STUDIO_DRAFT_SHAPE,
  meta: { name: '', currency, language },
  records: [],
  boardCards: [],
  roles: [],
  imports: [],
  sources: [],
  notes: [],
  notYet: [],
  from: [],
  dismissedGaps: [],
  checks: [],
});

